import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await api.post("/newsletter", { email });
      toast.success("Subscribed — thank you.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0E1530] text-white mt-32" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img
              src="/brand/b7-logo-transparent.png"
              alt="B7 Search & Selection"
              className="h-16 w-auto mb-3 brightness-0 invert"
            />
            <h2 className="font-serif text-5xl sm:text-6xl leading-none tracking-tight">
              Business driven.<br />
              <span className="text-[#2EA3F2]">People focused.</span>
            </h2>
            <p className="text-sm text-white/65 mt-8 max-w-md leading-relaxed">
              Specialist recruitment for the global construction sector — placing talent across
              the UK, Europe and the Middle East in Quantity Surveying, Commercial, Contracts
              &amp; Claims, Dispute Resolution, Planning, Programme &amp; Project Management.
            </p>
          </div>

          <div className="lg:col-span-2">
            <div className="overline-light">Explore</div>
            <ul className="mt-6 space-y-3 text-sm">
              <li><Link to="/about" className="link-underline text-white/80" data-testid="footer-about-link">About</Link></li>
              <li><Link to="/services" className="link-underline text-white/80" data-testid="footer-services-link">Services</Link></li>
              <li><Link to="/insights" className="link-underline text-white/80" data-testid="footer-insights-link">Insights</Link></li>
              <li><Link to="/contact" className="link-underline text-white/80" data-testid="footer-contact-link">Contact</Link></li>
              <li><Link to="/terms/uk" className="link-underline text-white/80" data-testid="footer-terms-uk-link">Terms (UK)</Link></li>
              <li><Link to="/terms/middle-east" className="link-underline text-white/80" data-testid="footer-terms-me-link">Terms (UAE / KSA)</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline-light">Engage</div>
            <ul className="mt-6 space-y-3 text-sm">
              <li><Link to="/vacancies" className="link-underline text-white/80" data-testid="footer-vacancies-link">Vacancies</Link></li>
              <li><Link to="/submit-cv" className="link-underline text-white/80" data-testid="footer-cv-link">Submit CV</Link></li>
              <li>
                <a
                  href="https://www.linkedin.com/company/b7-search-selection/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 link-underline text-white/80"
                  data-testid="footer-linkedin-link"
                >
                  <Linkedin size={14} /> LinkedIn
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline-light">Newsletter</div>
            <p className="text-sm text-white/65 mt-6 mb-4">
              Quarterly market briefs on construction recruitment.
            </p>
            <form onSubmit={subscribe} className="flex items-center border-b border-white/30" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                data-testid="newsletter-email-input"
                className="bg-transparent flex-1 py-3 outline-none text-sm text-white placeholder:text-white/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="p-2 text-white hover:text-[#2EA3F2] transition-colors"
                aria-label="Subscribe"
              >
                <ArrowUpRight size={20} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-white/55">
          <div data-testid="footer-address">
            Tameem House, Al Barsha Heights<br />Dubai, United Arab Emirates
          </div>
          <div className="md:text-right" data-testid="footer-copyright">
            © {new Date().getFullYear()} B7 Search &amp; Selection. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
